import React, { useRef, useEffect, useState } from "react";
import { DateTimePicker, Application } from "react-rainbow-components";
import "./style.css";

const containerStyles = {
  maxWidth: 400,
  display: "none",
};

type Props = {
  value: Date | null;
  setValue: (value: Date) => void;
  label?: string;
  formatStyle?: "large" | "medium" | "small";
  okLabel?: string;
  cancelLabel?: string;
  isDisabled?: boolean;
  timeOnly?: boolean;
  dateFormatOptions?: Intl.DateTimeFormatOptions;
  error?: {
    valid: boolean;
    message: string;
  };
  hideValidation?: boolean;
};

const theme = {
  rainbow: {
    palette: {
      brand: "#FEC307",
    },
  },
};

const DateAndTimePicker: React.FC<Props> = ({ value , setValue, label = "Date and Time", formatStyle = "large", okLabel = "OK", cancelLabel = "Cancel", isDisabled, timeOnly, error, dateFormatOptions, hideValidation }) => {
  const dateTimePickerRef = useRef(null);

  const testResult = !error ? "valid" : error.valid ? "valid" : "invalid" ;
  const inputClass = `is-${testResult}`
  const labelClass = `${testResult}-feedback`

  const openModal = () => {
    if (dateTimePickerRef.current && !isDisabled) {
      const dateTimePicker = dateTimePickerRef.current;
      if (dateTimePicker && dateTimePicker.fieldRef && dateTimePicker.fieldRef.current) {
        dateTimePicker.fieldRef.current.click();
      }
    }
  };

  const [formattedValue, setFormattedValue] = useState("");
  
  const formatDateTime = (date: Date | null) => {
    if (!date) return "";
    // Function to format the date
    const defaultOptions = {
      weekday: timeOnly ? undefined : "long" as const,
      month: "short" as const,
      day: "numeric" as const,
      year: "numeric" as const,
      hour: "numeric" as const,
      minute: "numeric" as const,
      hour12: true,
    };

    const formatOptions = dateFormatOptions ? dateFormatOptions  : defaultOptions;

    return new Intl.DateTimeFormat("en-US", formatOptions).format(date);
  };

  const formatTime = (date: Date) => {
    // Function to format the time
    const options = {
      hour: "numeric" as const,
      minute: "numeric" as const,
      hour12: true,
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  useEffect(() => {
    if (value) {
      if (timeOnly) {
        const formattedTime = formatTime(value);
        setFormattedValue(formattedTime);
      } else {
        const formattedDate = formatDateTime(value);
        setFormattedValue(formattedDate);
      }
    } else {
      setFormattedValue("");
    }
  }, [value, timeOnly]);

  // NOTES: We are using a useRef to open the modal when the user clicks on the input field.
  // There is a chance with updating the react-rainbow-components this functionality will break.

  const defaultDate = new Date();
  defaultDate.setHours(0, 0, 0, 0);
  
  

  return (
    <Application theme={theme}>
      <div className="">
        <div className="form-floating">
          <input type="text" name={"date-picker"} id={"date-picker"} className={`form-control ${hideValidation ? "" : inputClass}`} placeholder="Date" value={formattedValue} readOnly onClick={openModal} disabled={isDisabled} />

          <label htmlFor="date-picker" className={`floating`}>
            {label}
          </label>
          { error && !error?.valid && <span className={labelClass}>{error?.message}</span> }
        </div>
        <div className="rainbow-align-content_center rainbow-m-vertical_large rainbow-p-horizontal_small rainbow-m_auto" style={containerStyles}>
          <DateTimePicker ref={dateTimePickerRef} label={label} value={(value !== null) ? value : defaultDate} onChange={setValue} formatStyle={formatStyle} okLabel={okLabel} cancelLabel={cancelLabel} borderRadius="semi-square" />
        </div>
      </div>
    </Application>
  );
};

export default DateAndTimePicker;
